import { useMutation } from "@tanstack/react-query";
import { type FormEvent, type KeyboardEvent, useState } from "react";

import { type ApiCustomer, ApiError, createCustomer, updateCustomer } from "../lib/api";
import { parseTagsCsv } from "../lib/tags";
import { Badge, Button, Input, Modal, Textarea } from "./ui";

interface CustomerFormModalProps {
  mode: "create" | "edit";
  customer?: ApiCustomer;
  onClose: () => void;
  onSuccess: () => void;
}

type CustomerField = "name" | "phone" | "email" | "line_id" | "memo" | "tags";
type CustomerErrors = Partial<Record<CustomerField, string>>;

export function CustomerFormModal({
  mode,
  customer,
  onClose,
  onSuccess,
}: CustomerFormModalProps): JSX.Element {
  const [form, setForm] = useState(() => initialForm(customer));
  const [errors, setErrors] = useState<CustomerErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const tags = parseTagsCsv(form.tags);
  const mutation = useMutation({
    mutationFn: () =>
      mode === "create"
        ? createCustomer(buildInput(form))
        : updateCustomer(requiredCustomer(customer).id, buildInput(form)),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error) => applyApiError(error, setErrors, setGeneralError),
  });

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (mutation.isPending) return;
    const nextErrors = validate(form);
    setErrors(nextErrors);
    setGeneralError(null);
    if (Object.keys(nextErrors).length === 0) mutation.mutate();
  };
  const updateField = (field: CustomerField, value: string): void => {
    setForm({ ...form, [field]: value });
    setErrors((current) => withFieldError(current, field, validateField(field, value)));
  };

  return (
    <Modal
      open
      title={mode === "create" ? "新規顧客" : "顧客編集"}
      onClose={onClose}
      className="max-w-lg"
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBanner message={generalError} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="氏名"
            value={form.name}
            error={errors.name}
            required
            autoFocus
            onChange={(v) => updateField("name", v)}
          />
          <Field
            label="電話"
            value={form.phone}
            error={errors.phone}
            onChange={(v) => updateField("phone", v)}
          />
          <Field
            label="メール"
            type="email"
            value={form.email}
            error={errors.email}
            onChange={(v) => updateField("email", v)}
          />
          <Field
            label="LINE ID"
            value={form.line_id}
            error={errors.line_id}
            onChange={(v) => updateField("line_id", v)}
          />
        </div>
        <TagInputField
          tags={tags}
          value={tagInput}
          error={errors.tags}
          onInputChange={setTagInput}
          onAdd={(value) => {
            setForm((current) => ({
              ...current,
              tags: addTag(parseTagsCsv(current.tags), value).join(","),
            }));
            setTagInput("");
          }}
          onRemove={(value) => {
            setForm((current) => ({
              ...current,
              tags: parseTagsCsv(current.tags)
                .filter((tag) => tag !== value)
                .join(","),
            }));
          }}
        />
        <TextArea
          label="メモ"
          value={form.memo}
          error={errors.memo}
          onChange={(v) => updateField("memo", v)}
        />
        <Actions pending={mutation.isPending} mode={mode} onClose={onClose} />
      </form>
    </Modal>
  );
}

function initialForm(customer: ApiCustomer | undefined): Record<CustomerField, string> {
  return {
    name: customer?.name ?? "",
    phone: customer?.phone ?? "",
    email: customer?.email ?? "",
    line_id: customer?.line_id ?? "",
    memo: customer?.memo ?? "",
    tags: customer?.tags ?? "",
  };
}

function addTag(currentTags: ReadonlyArray<string>, value: string): string[] {
  const trimmed = value.trim();
  if (trimmed.length === 0 || currentTags.includes(trimmed)) return [...currentTags];
  return [...currentTags, trimmed];
}

function validate(form: Record<CustomerField, string>): CustomerErrors {
  const errors: CustomerErrors = {};
  for (const key of Object.keys(form) as CustomerField[]) {
    const message = validateField(key, form[key]);
    if (message !== undefined) errors[key] = message;
  }
  return errors;
}

function validateField(field: CustomerField, value: string): string | undefined {
  if (field === "name" && value.trim().length === 0) return "氏名は必須です";
  if (field !== "email" || value.trim().length === 0) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ? undefined
    : "メールアドレスの形式が正しくありません";
}

function withFieldError(
  current: CustomerErrors,
  field: CustomerField,
  message: string | undefined,
): CustomerErrors {
  const next = { ...current };
  if (message === undefined) {
    delete next[field];
    return next;
  }
  next[field] = message;
  return next;
}

function buildInput(form: Record<CustomerField, string>): Parameters<typeof createCustomer>[0] {
  const input: Parameters<typeof createCustomer>[0] = { name: form.name.trim() };
  for (const key of ["phone", "email", "line_id", "memo"] as const) {
    const value = form[key].trim();
    if (value.length > 0) input[key] = value;
  }
  input.tags = parseTagsCsv(form.tags).join(",");
  return input;
}

function requiredCustomer(customer: ApiCustomer | undefined): ApiCustomer {
  if (customer === undefined) throw new Error("customer is required");
  return customer;
}

function applyApiError(
  error: unknown,
  setErrors: (errors: CustomerErrors) => void,
  setGeneralError: (message: string | null) => void,
): void {
  if (!(error instanceof ApiError)) {
    setGeneralError("保存に失敗しました。");
    return;
  }
  const fieldErrors: CustomerErrors = {};
  for (const detail of error.details ?? []) {
    if (isCustomerField(detail.path)) fieldErrors[detail.path] = detail.message;
  }
  setErrors(fieldErrors);
  setGeneralError(Object.keys(fieldErrors).length === 0 ? error.message : null);
}

function isCustomerField(path: string): path is CustomerField {
  return ["name", "phone", "email", "line_id", "memo", "tags"].includes(path);
}

function TagInputField({
  tags,
  value,
  onInputChange,
  onAdd,
  onRemove,
  error,
}: {
  tags: ReadonlyArray<string>;
  value: string;
  onInputChange: (value: string) => void;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  error?: string | undefined;
}): JSX.Element {
  return (
    <label className="block text-xs font-medium text-text-secondary">
      タグ
      <div className="mt-1 flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 shadow-soft focus-within:border-primary focus-within:ring-2 focus-within:ring-[var(--color-focus-ring)]">
        {tags.map((tag) => (
          <TagChip key={tag} tag={tag} onRemove={onRemove} />
        ))}
        <input
          type="text"
          value={value}
          onChange={(e) => onTagInputChange(e.target.value, onInputChange, onAdd)}
          onKeyDown={(e) => onTagInputKeyDown(e, value, onAdd)}
          onBlur={() => onAdd(value)}
          className="min-w-24 flex-1 border-0 bg-transparent p-0 text-sm text-text-primary outline-none"
          placeholder="タグを入力"
        />
      </div>
      <FieldError message={error} />
    </label>
  );
}

function TagChip({
  tag,
  onRemove,
}: { tag: string; onRemove: (value: string) => void }): JSX.Element {
  return (
    <Badge variant="primary" className="gap-1">
      #{tag}
      <button
        type="button"
        onClick={() => onRemove(tag)}
        aria-label={`${tag} を削除`}
        className="leading-none"
      >
        ×
      </button>
    </Badge>
  );
}

function onTagInputChange(
  value: string,
  onInputChange: (value: string) => void,
  onAdd: (value: string) => void,
): void {
  if (!/[,、\s]/.test(value)) {
    onInputChange(value);
    return;
  }
  for (const tag of value.split(/[,、\s]+/)) onAdd(tag);
}

function onTagInputKeyDown(
  e: KeyboardEvent<HTMLInputElement>,
  value: string,
  onAdd: (value: string) => void,
): void {
  if (e.key !== "Enter" && e.key !== "," && e.key !== " " && e.key !== "、") return;
  e.preventDefault();
  onAdd(value);
}

function Field({
  label,
  value,
  onChange,
  error,
  type = "text",
  required = false,
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  type?: "text" | "email";
  required?: boolean;
  autoFocus?: boolean;
}): JSX.Element {
  const id = `customer-${label}`;
  return (
    <div className="block text-xs font-medium text-text-secondary">
      <label htmlFor={id}>{label}</label>
      <Input
        id={id}
        autoFocus={autoFocus}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        error={error}
        className="mt-1"
      />
    </div>
  );
}

function TextArea({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
}): JSX.Element {
  const id = `customer-${label}`;
  return (
    <div className="block text-xs font-medium text-text-secondary">
      <label htmlFor={id}>{label}</label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        error={error}
        className="mt-1"
      />
    </div>
  );
}

function FieldError({ message }: { message?: string | undefined }): JSX.Element | null {
  return message === undefined ? null : <p className="mt-1 text-xs text-danger">{message}</p>;
}

function ErrorBanner({ message }: { message: string | null }): JSX.Element | null {
  if (message === null) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-danger bg-danger-bg px-3 py-2 text-sm text-danger"
    >
      {message}
    </p>
  );
}

function Actions({
  pending,
  mode,
  onClose,
}: {
  pending: boolean;
  mode: "create" | "edit";
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="flex justify-end gap-2 pt-1">
      <Button variant="secondary" onClick={onClose} disabled={pending}>
        キャンセル
      </Button>
      <Button type="submit" loading={pending}>
        {mode === "create" ? "作成" : "保存"}
      </Button>
    </div>
  );
}
